/* eslint-disable react-refresh/only-export-components */

import React, { useContext, useMemo } from "react";
import type { ReactNode } from "react";
import { useU2000GridStateHandlers } from "../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import { useU2000SearchStateHandlers } from "../u2000Core/reducer/formReducer/U2000SearchStateHandlers";
import type { U2000GridStateHandlersReturn } from "../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import type { U2000SearchStateHandlersReturn } from "../u2000Core/reducer/formReducer/U2000SearchStateHandlers";
import { useU2000LUStateHandlers } from "../u2000Core/reducer/formReducer/U2000LUHandlers";
import type { U2000LUState } from "../u2000Core/reducer/formReducer/U2000LUState";
import type { U3101Employee } from "./U3101Service";

// Type for initLU (from LUState.initLU)
export type InitLU = U2000LUState["personalization"] & { launchSearch: boolean; hideSearch: boolean; nbRecordPerPage: number };

export interface U3101ContextType {
  luStateHandlers: ReturnType<typeof useU2000LUStateHandlers>;
  searchStateHandlers: U2000SearchStateHandlersReturn;
  gridStateHandlers: U2000GridStateHandlersReturn<U3101Employee>;
}

interface U3101ContextProviderProps {
  initLU: InitLU;
  children: ReactNode;
}

const U3101Context = React.createContext<U3101ContextType | undefined>(undefined);

export const U3101ContextProvider: React.FC<U3101ContextProviderProps> = ({ initLU, children }) => {
  const U2000LUStateHandlers = useU2000LUStateHandlers({ initLU: initLU as InitLU & U2000LUState });
  const U2000SearchStateHandlers = useU2000SearchStateHandlers({ luName: "u3101", launchSearch: initLU.launchSearch, hideSearch: initLU.hideSearch });
  const U2000GridStateHandlers = useU2000GridStateHandlers<U3101Employee>({ nbRecordPerPage: initLU.nbRecordPerPage ?? 50 });

  const contextValue = useMemo<U3101ContextType>(
    () => ({
      luStateHandlers: U2000LUStateHandlers,
      searchStateHandlers: U2000SearchStateHandlers,
      gridStateHandlers: U2000GridStateHandlers,
    }),
    [U2000GridStateHandlers, U2000SearchStateHandlers, U2000LUStateHandlers]
  );

  return <U3101Context.Provider value={contextValue}>{children}</U3101Context.Provider>;
};

export function useU3101Context(): U3101ContextType {
  const context = useContext(U3101Context);
  if (!context) {
    throw new Error("useU3101Context must be used within a U3101ContextProvider");
  }
  return context;
}
